import { TravelWalletEnum } from "@b2bportal/lodging-api";
import type { LodgingContext } from "@checkout/index";
import { OfferPaymentType, WalletVoucherStatusEnum } from "@hopper-b2b/types";
import dayjs from "dayjs";
import type { State } from "xstate";
import { ParentState } from "../../../types/common";
import type { ParentContextWithWallet } from "./types";

type StateType = State<ParentContextWithWallet>;
type StateWithoutValue = Pick<StateType, "context">;

export const getWalletOfferError = (state: StateWithoutValue) => {
  return state.context[ParentState.wallet]?.error;
};

export const getWalletOffersId = (state: StateWithoutValue) => {
  return [state.context[ParentState.wallet]?.selectedOffer.id].filter((o) => o);
};

export const getWalletOfferFromId = (
  state: StateWithoutValue,
  offerId: string
) => {
  return [
    state.context[ParentState.wallet].offers?.find((o) => o.id === offerId),
  ].filter((o) => o);
};

export const getWalletOffersChoice = (state: StateWithoutValue) => {
  return [state.context[ParentState.wallet].selectedOffer].filter((o) => o);
};

export const getWalletOffer = (state: StateWithoutValue) => {
  return state.context[ParentState.wallet].selectedOffer;
};

export const getOfferStatus = (state: StateWithoutValue) => {
  return state.context[ParentState.wallet].offerStatus;
};

export const getIsWalletOfferFetched = (state: StateWithoutValue) =>
  state.context[ParentState.wallet].fetchedOffers;

export const getIsWalletOfferApplied = (state: StateWithoutValue) =>
  state.context[ParentState.wallet].offerApplied;

export const getIsWalletVisited = (state: StateWithoutValue) =>
  state.context[ParentState.wallet].visited;

export const getWalletOffers = (state: StateWithoutValue) =>
  state.context[ParentState.wallet].offers;

export const getWalletOffersNoCredits = (state: StateWithoutValue) =>
  state.context[ParentState.wallet].offers?.filter(
    (offer) => offer.payment.value.paymentType === TravelWalletEnum.Offer
  );

export const getWalletOffersOnly = (state: StateWithoutValue) =>
  state.context[ParentState.wallet].offers?.filter(
    (offer) => offer.payment.paymentType === OfferPaymentType.Offer
  );

export const getHasWalletOffersAvailable = (state: StateWithoutValue) =>
  state.context[ParentState.wallet].offers?.length > 0;

export const getWalletTrackingProperties = (state: StateWithoutValue) => {
  const offer = state.context[ParentState.wallet].selectedOffer;

  return {
    offer_id: offer?.id,
    offer_name: offer?.offerContent?.title,
    // program_id: offer.offerContent.title,
    offer_applicable: true,
    offer_status: state.context[ParentState.wallet].offerStatus,
    // offer_discount if percentage, is based off the cheapest hotel
    // these two won't be available until checkout
    // offer_discount: 50,
    // offer_discount_usd: 50,
    offer_expiration_days: dayjs(offer?.expiresOn).diff(dayjs(), "days"),
    ...(offer?.percentage
      ? {
          offer_discount_percentage_value: offer.percentage * 100,
          offer_discount_type: "percentage",
        }
      : { offer_discount_type: "cash" }),
    ...offer?.trackingPropertiesV2,
  };
};

export const getCheckoutOfferSelectionTrackingProperties =
  (state: StateWithoutValue) => (props: { offerActive: boolean }) => {
    const offer = state.context[ParentState.wallet].selectedOffer;
    return {
      offer_id: offer?.id,
      offer_name: offer?.offerContent?.title,
      // program_id: offer.offerContent.title,
      offer_applicable: true,
      offer_status: props.offerActive
        ? WalletVoucherStatusEnum.EXPLICITLY_ACCEPTED
        : WalletVoucherStatusEnum.EXPLICITLY_DECLINED,
      // offer_discount if percentage, is based off the cheapest hotel
      // these two won't be available until checkout
      // offer_discount: 50,
      // offer_discount_usd: 50,
      offer_expiration_days: dayjs(offer?.expiresOn).diff(dayjs(), "days"),
      ...(offer?.percentage
        ? {
            offer_discount_percentage_value: offer.percentage * 100,
            offer_discount_type: "percentage",
          }
        : { offer_discount_type: "cash", offer_discount: offer?.amount }),
      ...offer?.trackingPropertiesV2,
    };
  };

export const getApplicableOfferTrackingProperties =
  (state: StateWithoutValue) => (props?: { isActive?: boolean }) => {
    return {
      offer_status: state.context[ParentState.wallet].offerStatus,
      // deferred is opposite of the offer being active
      all_discounts_deferred: !props.isActive,
    };
  };

export const getCheckoutTrackingProperties = ({
  context,
}: {
  context: LodgingContext;
}) => {
  if (context) {
    const walletContext = context[ParentState.wallet];
    const offerFromAvailability = context[ParentState.lodgingShop].bestOffer;
    if (walletContext.selectedOffer) {
      return {
        properties: {
          offer_status: walletContext.offerStatus,
          offer_applicable: walletContext.offers?.length > 0,
          offer_id: walletContext.selectedOffer?.id,
          offer_expiration_days: dayjs(
            walletContext.selectedOffer?.expiresOn
          ).diff(dayjs(), "days"),
          ...(walletContext.selectedOffer?.percentage
            ? {
                offer_discount_percentage_value:
                  walletContext.selectedOffer.percentage * 100,
                offer_discount_type: "percentage",
                // discount is what's in the balance, we only know this in checkout for percentages
                offer_discount:
                  context[ParentState.cartQuote]?.quoteBreakdown?.paymentTotal
                    ?.fiat?.value || 0,
              }
            : {
                offer_discount_type: "cash",
                offer_discount: walletContext.selectedOffer?.amount,
              }),
          ...walletContext.selectedOffer?.trackingPropertiesV2?.properties,
          ...walletContext.trackingProperties?.properties,
        },
        encryptedProperties:
          walletContext.selectedOffer?.trackingPropertiesV2
            ?.encryptedProperties,
      };
    } else if (offerFromAvailability) {
      // TODO(Nate): Clean this up post Nubank-DMOs
      return {
        properties: {
          offer_status: walletContext.offerStatus,
          // Default to true if the offer exists
          offer_applicable: true,
          offer_id: offerFromAvailability.id,
          offer_expiration_days: dayjs(offerFromAvailability.expiresOn).diff(
            dayjs(),
            "days"
          ),
          // We only offer percentage discounts
          offer_discount_type: "percentage",
          offer_discount_percentage_value:
            offerFromAvailability.percentage * 100,
          offer_discount:
            context[ParentState.cartQuote]?.quoteBreakdown?.paymentTotal?.fiat
              ?.value || 0,
          offer_discount_currency: offerFromAvailability.amount.currency,
        },
      };
    }
  }
  return {};
};
